import { ShopCover } from "./ShopCover";
/**
 * Canonical store card (web).
 *
 * Supersedes 7 divergent implementations:
 *   Home.tsx:124, OrderStores.tsx:166 (grid), OrderStores.tsx:244 (list),
 *   Businesses.tsx:96, OrderStoreDetail.tsx, MultiOrderCompanionPicker,
 *   BusinessOrderStore.
 *
 * Business logic (open/closed, sponsorship, offers, fees) is unchanged —
 * it now lives once in ./store-utils.
 */
import { Link } from "wouter";
import { ShoppingBag, Star, Truck } from "lucide-react";
import { cn } from "@/lib/utils";
import { Chip, ClosedOverlay, OfferBanner } from "./primitives";
import {
  formatNextOpen, formatOfferLabel, offerDisplayLabel, hasFreeDelivery, isSponsored,
  offerIcon, pence, storeHref, storeIsOpen, type StoreItem,
} from "./store-utils";

function StarRating({ avgRating, reviewCount }: { avgRating?: number; reviewCount?: number }) {
  if (!avgRating || !reviewCount) return null;
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-600">
      <Star size={11} className="fill-amber-400 text-amber-400" />
      {avgRating.toFixed(1)}
      <span className="text-slate-400 font-normal">({reviewCount})</span>
    </span>
  );
}

function StoreChips({ store }: { store: StoreItem }) {
  const deliveryMins = store.currentDeliveryMins ?? store.deliveryTimeMins;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {store.offersDelivery && (
        <Chip tone="neutral" size="xs" icon={<Truck size={9} />}>{deliveryMins} min</Chip>
      )}
      {store.offersCollection && <Chip tone="neutral" size="xs">Collect</Chip>}
      {hasFreeDelivery(store) && <Chip tone="success" size="xs">Free delivery</Chip>}
      {store.deliveryFeeLabel && !hasFreeDelivery(store) && <Chip tone="neutral" size="xs">{store.deliveryFeeLabel}</Chip>}
      {store.minOrderPence > 0 && (
        <Chip tone="neutral" size="xs">Min {pence(store.minOrderPence)}</Chip>
      )}
    </div>
  );
}

export function StoreCard({
  store, variant = "grid", className,
}: {
  store: StoreItem;
  /** grid = page grids · rail = horizontal carousel · list = dense row */
  variant?: "grid" | "rail" | "list";
  className?: string;
}) {
  const photoUrl = store.bannerUrl || store.coverPosterUrl;
  const open = storeIsOpen(store);
  const sponsored = isSponsored(store);
  const closedLabel = formatNextOpen(store.nextOpenTime, store.acceptingOrders);
  const offers = store.activeOffers ?? [];

  if (variant === "list") {
    return (
      <Link href={storeHref(store)}><div className={cn(sponsored && "boosted-card !rounded-[26px]")}>
        <div className={cn(
          "relative flex gap-3 bg-white rounded-[24px] border p-4 transition-shadow hover:shadow-md cursor-pointer",
          sponsored ? "border-amber-300" : "border-slate-100",
          !open && !sponsored && "opacity-60", className,
        )}>
          {photoUrl && <div className="relative w-24 h-24 shrink-0 rounded-xl overflow-hidden bg-slate-100">
            {photoUrl
              ? <ShopCover store={store} className="w-full h-full" />
              : <div className="w-full h-full grid place-items-center"><ShoppingBag size={22} className="text-slate-300" /></div>}
            {!open && <ClosedOverlay label={closedLabel} />}
            {store.imageUrl && <img src={store.imageUrl} alt={`${store.name} logo`} className="absolute bottom-3 left-3 w-14 h-14 rounded-xl border-2 border-white bg-white shadow-md object-contain" loading="lazy"/>}
          </div>}
          <div className="min-w-0 flex-1">
            {!(store.bannerUrl || store.coverPosterUrl) && store.imageUrl && <img src={store.imageUrl} alt={`${store.name} logo`} className="float-right ml-3 mb-2 w-14 h-14 rounded-xl border border-slate-200 bg-white object-contain" loading="lazy"/>}
            {!open && !photoUrl && <p className="text-xs font-semibold text-slate-500 mb-1">{closedLabel}</p>}
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-bold text-slate-900 truncate">{store.name}</h3>
              {sponsored && <Chip tone="gold" size="xs">Sponsored</Chip>}
            </div>
            <p className="text-[13px] text-slate-500 truncate">{store.category} · {store.town}</p>
          <div className="mt-1.5"><StarRating avgRating={store.avgRating} reviewCount={store.reviewCount} /></div>
            <div className="mt-2"><StoreChips store={store} /></div>
          </div>
        </div>
      </div></Link>
    );
  }

  const isRail = variant === "rail";
  return (
    <Link href={storeHref(store)}><div className={cn(sponsored && "boosted-card !rounded-[26px]")}>
      <div className={cn(
        "relative group bg-white rounded-[24px] overflow-hidden border border-slate-200/80 shadow-[0_4px_20px_#10233d08] transition-shadow hover:shadow-md cursor-pointer",
        "border-transparent",
        !open && !sponsored && "opacity-60",
        isRail && "w-[220px] shrink-0",
        className,
      )}>
        {photoUrl && <div className={cn("relative bg-slate-100 overflow-hidden", isRail ? "h-36" : "aspect-[1.9/1]")}>
          {photoUrl ? (
            <ShopCover store={store} alt={`${store.name} banner`} className="w-full h-full transition-transform duration-500 group-hover:scale-105" />
          ) : (
            <div className="w-full h-full grid place-items-center"><ShoppingBag size={30} className="text-slate-300" /></div>
          )}
          {!open && <ClosedOverlay label={closedLabel} />}
          {sponsored && (
            <div className="absolute top-2 left-2"><Chip tone="gold" size="xs">Sponsored</Chip></div>
          )}
          {store.multiStoreEligible && open && (
            <div className="absolute top-2 right-2"><Chip variant="overlay" size="xs">Multi-shop</Chip></div>
          )}
          {/* Shop logo overlaid bottom-left of the header image (only when a distinct
              banner is the main image, so we don't duplicate the logo). */}
          {store.bannerUrl && store.imageUrl && (
            <div className={cn(
              "absolute bottom-2 left-2 rounded-lg overflow-hidden border-2 border-white bg-white shadow-md",
              isRail ? "w-9 h-9" : "w-12 h-12",
            )}>
              <img src={store.imageUrl} alt={`${store.name} logo`} className="w-full h-full object-contain" loading="lazy" />
            </div>
          )}
        </div>}
        <div className="p-4">
          {!(store.bannerUrl || store.coverPosterUrl) && store.imageUrl && <img src={store.imageUrl} alt={`${store.name} logo`} className="float-right ml-3 mb-2 w-14 h-14 rounded-xl border border-slate-200 bg-white object-contain" loading="lazy"/>}
          {sponsored && !photoUrl && <Chip tone="gold" size="xs">Sponsored</Chip>}
          {!open && !photoUrl && <p className="text-xs font-semibold text-slate-500 mb-1">{closedLabel}</p>}
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-bold text-slate-900 truncate leading-tight">{store.name}</h3>
          </div>
          <p className="text-[12.5px] text-slate-500 truncate mt-0.5">{store.category} · {store.town}</p>
          <div className="mt-2 empty:hidden">{offers.length > 0 && (
          <OfferBanner compact>
            {offerDisplayLabel(offers[0]!)}
            {offers[0]!.minOrderPence > 0 ? ` · min ${pence(offers[0]!.minOrderPence)}` : ""}
          </OfferBanner>
        )}</div>
          <div className="mt-1.5"><StarRating avgRating={store.avgRating} reviewCount={store.reviewCount} /></div>
          <div className="mt-2"><StoreChips store={store} /></div>
        </div>
      </div>
    </div></Link>
  );
}
